import { useEffect, useCallback, useRef, useMemo } from "react";

import useId from "./useId";

const useStatic = (StaticClass, config = {}) => {
  const instance = useRef(null);
  const configRef = useRef(config);

  useEffect(() => {
    configRef.current = config ?? {};
  }, [config]);

  const ref = useCallback(
    (node) => {
      // use latest config available
      const config = configRef.current;
      if (node) {
        if (instance.current === null) {
          instance.current = new StaticClass(node, config);
        } else {
          // eslint-disable-next-line no-unused-expressions
          instance.current.update?.(config);
        }
      }
    },
    [StaticClass]
  );

  useEffect(
    () => () => {
      if (instance.current) {
        // eslint-disable-next-line no-unused-expressions
        instance.current.destroy?.();
        instance.current = null;
      }
    },
    []
  );

  return [ref, instance];
};

// From https://www.jayfreestone.com/writing/react-portals-with-hooks/
function usePortal(id) {
  const rootElemRef = useRef(document?.createElement("div"));

  useEffect(
    function setupElement() {
      // Look for existing target dom element to append to
      const parentElem = document?.querySelector(`#${id}`);

      // Add the detached element to the parent
      // eslint-disable-next-line no-unused-expressions
      parentElem?.appendChild(rootElemRef.current);
      // This function is run on unmount
      return function removeElement() {
        // eslint-disable-next-line no-unused-expressions
        rootElemRef?.current.remove();
      };
    },
    [id]
  );

  function getRootElem() {
    if (!rootElemRef.current) {
      rootElemRef.current = document?.createElement("div");
    }
    return rootElemRef.current;
  }

  return getRootElem();
}

/**
 * Returns unique string for the `id` attribute.
 *
 * @param id Provided id
 * @param name Optional prefix
 */
// eslint-disable-next-line react-hooks/rules-of-hooks
const useUniqueId = (id, name) => useMemo(() => id ?? useId(name), [id, name]);

export { useStatic, usePortal, useUniqueId };
